import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Mail, CheckCircle2, ShieldAlert, Loader2 } from 'lucide-react';
import { useRouteLocale } from '@/hooks/useRouteLocale';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useTranslation } from '@/hooks/useTranslation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LoginHeader } from '@/components/login';
import { useAuth } from '@/hooks/useAuth';
import { UserHeader } from '@/components/customer/UserHeader';
import { USER_ROUTES, PUBLIC_ROUTES } from '@/routes';
import { verifyEmail, resendVerificationEmail } from '@/services/api/auth';

export default function EmailVerificationPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const locale = useRouteLocale();
  const currentLocale = locale || 'ro';

  usePageMeta({
    title: t('auth.emailVerification.metaTitle'),
    description: t('auth.emailVerification.metaTitle'),
    locale: locale,
    path: PUBLIC_ROUTES.emailVerification(locale),
    noindex: true,
  });

  const { user } = useAuth();
  const isAuthenticated = Boolean(user);

  const tokenParam = searchParams.get('token') || '';
  const emailParam =
    searchParams.get('email') ||
    (typeof window !== 'undefined' ? sessionStorage.getItem('pending_verification_email') || '' : '') ||
    user?.user_email ||
    '';

  const [emailInput, setEmailInput] = useState(emailParam);
  const [verifying, setVerifying] = useState(Boolean(tokenParam));
  const [verified, setVerified] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (!tokenParam) {
      return;
    }

    let cancelled = false;
    setVerifying(true);
    setVerifyError(null);

    verifyEmail(tokenParam, emailParam || undefined)
      .then((res) => {
        if (cancelled) return;
        setVerified(true);
        if (res.userEmail) {
          setEmailInput(res.userEmail);
        }
        sessionStorage.removeItem('pending_verification_email');
        toast.success(t('auth.emailVerification.verifiedSuccess'));
      })
      .catch(() => {
        if (cancelled) return;
        setVerifyError(t('auth.emailVerification.invalidOrExpired'));
      })
      .finally(() => {
        if (!cancelled) {
          setVerifying(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [tokenParam, emailParam, t]);

  const handleGoToLogin = () => {
    navigate(PUBLIC_ROUTES.login(currentLocale));
  };

  const handleGoBack = () => {
    navigate(PUBLIC_ROUTES.register(currentLocale));
  };

  const handlePrimaryAction = () => {
    if (isAuthenticated) {
      navigate(USER_ROUTES.events(currentLocale));
      return;
    }
    handleGoToLogin();
  };

  const handleResend = async () => {
    const targetEmail = emailInput.trim();
    if (!targetEmail) {
      toast.error(t('validation.emailRequired'));
      return;
    }

    try {
      setResending(true);
      await resendVerificationEmail(targetEmail);
      toast.success(t('auth.emailVerification.resendSuccess'));
    } catch {
      toast.error(t('common.error'));
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {isAuthenticated ? (
        <UserHeader currentLocale={currentLocale} active="events" />
      ) : (
        <LoginHeader currentLocale={currentLocale} action={{ label: t('auth.signIn'), onClick: handleGoToLogin }} />
      )}

      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="bg-card border rounded-lg p-8 shadow-sm">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-pink-100 dark:bg-pink-900/30 mb-4">
                {verifying ? (
                  <Loader2 className="h-8 w-8 text-pink-600 dark:text-pink-400 animate-spin" />
                ) : verified ? (
                  <CheckCircle2 className="h-8 w-8 text-green-600 dark:text-green-400" />
                ) : (
                  <Mail className="h-8 w-8 text-pink-600 dark:text-pink-400" />
                )}
              </div>
              <h1 className="text-2xl font-semibold text-foreground mb-2">
                {verified
                  ? t('auth.emailVerification.verifiedTitle')
                  : t('auth.emailVerification.title')}
              </h1>
              <p className="text-muted-foreground">
                {verified
                  ? t('auth.emailVerification.verifiedSubtitle')
                  : t('auth.emailVerification.subtitle')}
              </p>
            </div>

            {verifyError && (
              <div
                data-testid="email-verification-error"
                className="mb-6 flex items-start gap-3 p-4 bg-destructive/10 text-destructive rounded-lg border border-destructive/30"
              >
                <ShieldAlert className="h-5 w-5 mt-0.5 flex-shrink-0" />
                <p className="text-sm">{verifyError}</p>
              </div>
            )}

            {verified ? (
              <div
                data-testid="email-verified-banner"
                className="mb-6 flex items-start gap-3 p-4 bg-green-50 dark:bg-green-950/30 rounded-lg border border-green-200 dark:border-green-800"
              >
                <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-foreground">
                  {t('auth.emailVerification.verifiedSuccess')}
                </p>
              </div>
            ) : (
              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-3 p-4 bg-muted/50 rounded-lg">
                  <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-foreground">
                    {t('auth.emailVerification.checkEmail')}
                  </p>
                </div>

                <div className="flex items-start gap-3 p-4 bg-muted/50 rounded-lg">
                  <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-foreground">
                    {t('auth.emailVerification.clickLink')}
                  </p>
                </div>

                <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200/70">
                  <ShieldAlert className="h-5 w-5 text-amber-600 dark:text-amber-300 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-foreground">
                    {t('auth.emailVerification.limitedAccess')}
                  </p>
                </div>
              </div>
            )}

            <div className="space-y-3">
              <Button
                onClick={handlePrimaryAction}
                className="w-full"
                variant="default"
                data-testid="email-verification-primary-cta"
              >
                {isAuthenticated
                  ? t('auth.emailVerification.dashboardCta')
                  : t('auth.goToLogin')}
              </Button>

              {!isAuthenticated && !verified && (
                <Button
                  onClick={handleGoBack}
                  className="w-full"
                  variant="outline"
                >
                  {t('auth.backToRegister')}
                </Button>
              )}
            </div>

            {!verified && (
              <div className="mt-6 space-y-3 text-center">
                {!emailParam && (
                  <Input
                    type="email"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    placeholder={t('auth.emailPlaceholder')}
                    data-testid="resend-verification-email-input"
                  />
                )}
                <p className="text-xs text-muted-foreground">
                  {t('auth.emailVerification.didNotReceive')}{' '}
                  <button
                    type="button"
                    disabled={resending}
                    onClick={handleResend}
                    data-testid="resend-verification-button"
                    className="text-pink-600 dark:text-pink-400 hover:underline disabled:opacity-50"
                  >
                    {t('auth.emailVerification.resendEmail')}
                  </button>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
